﻿// © Microsoft Corporation. All rights reserved.

import { canUseDOM, getInnerHeight, getInnerWidth } from "@msnews/isomorphic-util";

// Wraps modern visibility api, as well as defines visibility for elements based on viewbox.

// a simple deferred promise object that delays execution of callbacks until the owner resolves. (see $.Deferred for jQuery's implementation, this is a subset)
function Deferred() {
    // the callbacks to execute when resolved
    let callbacks = [];

    // the state of the promise.  If we have already resolved we execute the delegates immediately.
    let isResolved = false;

    // the arguments used to resolve the promise.
    let resolvedArgs = null;

    // executes all the callbacks that need to be notified, giving them a ref to the arguments supplied.
    function flushCallbacks() {
        // we only flush if we are resolved
        if (isResolved) {
            const errors = [];

            // loop through the callbacks and execute.
            for (let index = 0; index < callbacks.length; index++) {
                try {
                    callbacks[index](resolvedArgs);
                } catch (e) {
                    // delay propagation of errors until all callbacks have been notified
                    errors.push(e);
                }
            }

            // clear the callbacks array
            callbacks = [];

            // throw the error collection if we received any.
            if (errors.length) {
                throw errors;
            }
        }
    }

    // resolves the promise
    function resolve() {
        isResolved = true;
        resolvedArgs = arguments;
        flushCallbacks();
    }

    // the promise function
    function promise() {
        return {

            // the happy path for callbacks
            then:
                function (callback) {
                    callbacks.push(callback);
                    flushCallbacks();
                },

            // subscribers listen here for when the promise was denied or resolved.
            always:
                function () {
                    return this.then(arguments);
                }
        };
    }

    // lets return our deferred object that.
    return {
        // the promise object
        promise: promise,
        // the resolve method.
        resolve: resolve
    };
}

// create our visibility deferred
// @ts-ignore
let visibilityDeferred = new Deferred();

// initialize the visibility property name
const visibilityProperty = getHiddenPropertyName();

// Flag to check if unload event has been triggered
// Page Visibility API removed 'unloaded' visbility state and now reports 'hidden' when browser is being unloaded.This breaks the wasBrowserHiddenAtLeastOnce logic.
// Flag is used to detect & ignore state change if unload event caused the 'hidden' visibilityState.
// Chrome 56 (as of 2/21/2017) has implemented the spec change and would probably land in Edge in the future releases.
// Link to spec - https://w3c.github.io/page-visibility/

let isUnloadEventInProgress = false;

if (canUseDOM() && window.addEventListener) {
    window.addEventListener("beforeunload", function () {
        isUnloadEventInProgress = true;
    });
}

// flag to indicate if Browser was hidden even once in a page impression
let wasBrowserHidden = false;

if (canUseDOM() && visibilityProperty) {
    // this preserves our vendor prefix
    const evtname = visibilityProperty.replace(/[H|h]idden/, "") + "visibilitychange";
    document.addEventListener(evtname, visibilityChanged);
}

// call it immediately to prime the deferred if we are already visibile.
visibilityChanged();

// returns the property name for document.hidden, which can contain the browser specific prefix, or null if not defined.
// hidden is part of modern browsers visibility api.
function getHiddenPropertyName() {
    if (!canUseDOM()) {
        return null;
    }

    const prefixes = ["webkit", "moz", "ms", "o"];

    // if 'hidden' is natively supported just return it
    if (typeof document.hidden !== "undefined") {
        return "hidden";
    }

    // otherwise loop over all the known prefixes until we find one
    for (let index = 0; index < prefixes.length; index++) {
        const prospectivePropertyName = prefixes[index] + "Hidden";
        if ((prospectivePropertyName) in document) {
            return prospectivePropertyName;
        }
    }
}

// determines if the page is hidden.
function isHidden() {
    if (!canUseDOM() || !visibilityProperty) {
        return false;
    }

    return document[visibilityProperty];
}

// handler for visibility changes.  Determines if we should resolve the promise.
function visibilityChanged() {
    if (!isHidden()) {
        visibilityDeferred.resolve();
    } else {
        // create a new one so we can fire when we are back.
        // @ts-ignore
        visibilityDeferred = new Deferred();
        // flag indicates that browser was minimised or hidden during the page impression

        if (!isUnloadEventInProgress) {
            wasBrowserHidden = true;
        }
    }
}

// returns if the browser was hidden at least once
function wasBrowserHiddenAtLeastOnce() {
    return (wasBrowserHidden ? 1 : 0);
}

// determines if an element is currently visible to the user
function isElementTrulyVisible(element, windowRight, windowLeft, windowHeight) {
    if (isHidden() || !element) {
        return false;
    }

    windowHeight = windowHeight || getInnerHeight();
    windowRight = windowRight || getInnerWidth();

    if (!windowLeft && windowLeft !== 0) {
        windowLeft = 0;
        const contentArea = document.getElementById("content");

        if (contentArea) {
            const contentRect = contentArea.getBoundingClientRect();
            windowRight = contentRect.right;
            windowLeft = contentRect.left;
        }
    }

    let boxVisible = false;

    const rec = element.getBoundingClientRect();

    const elementHeightVisible = (rec.top >= 0 || rec.bottom >= 0) && (rec.top < windowHeight || rec.bottom < windowHeight);
    const elementWidthVisible = (rec.left >= windowLeft || rec.right > windowLeft) && (rec.left < windowRight || rec.right <= windowRight);

    boxVisible = rec.height !== 0 || rec.width !== 0;

    return boxVisible && (elementHeightVisible && elementWidthVisible);
}

/// <summary>Notifies the callee when we return to visibile state.  Returns immediately if we are currently visible</summary>
/// <returns>A JQuery Promise that resolves when visible</returns>
function notifyWhenPageVisible() {
    // return our current promise.
    return visibilityDeferred.promise();
}

// return the visibility object being returned
export default {
    // returns true if the document is hidden
    isHidden: isHidden,
    // returns a jquery promise that resolves the next time (or immediately) the page becomes visible
    notifyWhenPageVisible: notifyWhenPageVisible,
    // returns true if the page is visible && element within viewport
    isElementTrulyVisible: isElementTrulyVisible,
    // returns true if page was hidden even once
    wasBrowserHiddenAtLeastOnce: wasBrowserHiddenAtLeastOnce
};
